import { ApplicationRef, Component, inject, OnInit, PLATFORM_ID, DOCUMENT } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Meta, Title } from '@angular/platform-browser';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';


import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { environment } from '../environments/environment';
import { SolanaInitialisationService } from './services/sol-services/solana-initialise.service';
import { faqItems, getBrowserName, getCountryFromTimeZone, getReferrer, handleLastClickTracking } from './shared/constants/app-enums';
import { allLanguages } from './shared/constants/languages';
import { AnalyticsService } from './services/analytics.service';

declare global {
  interface Window {
    dataLayer: { [key: string]: string | number | boolean | undefined }[];
    VWO: any; // VWO A/B testing SDK — no published TypeScript types; accepted exception per CLAUDE.md
  }
}
export var selectedLang: string;

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent implements OnInit {
  canRender: boolean = true;
  onStaking = false;
  base = environment.domain;

  private readonly dom = inject(DOCUMENT);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly meta = inject(Meta);
  private readonly title = inject(Title);
  private readonly translate = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly solService = inject(SolanaInitialisationService);
  private readonly analyticsService = inject(AnalyticsService);

  constructor() {
    inject(ApplicationRef).isStable.pipe(takeUntilDestroyed()).subscribe((isStable) => (this.canRender = isStable || this.canRender));

    handleLastClickTracking(['clickId', 'source', 'tid', 'pid']);
    getReferrer();

    if (typeof window !== 'undefined') {
      const browserType = getBrowserName();
      const userCountry = getCountryFromTimeZone();
      this.analyticsService.vwoData('pageLand', null, false);
      this.analyticsService.vwoData('browserTypeAndUserCountry', { browserType, userCountry });
    }

    this.router.events.pipe(filter(event => event instanceof NavigationEnd), takeUntilDestroyed()).subscribe((event: NavigationEnd) => {
      this.onStaking = event.url.includes('staking');
      this.updateSEOData(true);
    });

    this.translate.onLangChange.pipe(takeUntilDestroyed()).subscribe((params: LangChangeEvent) => {
      selectedLang = params.lang;
      const langObj = allLanguages.filter((x) => x.value === selectedLang)[0];
      this.updateSEOData(false);
      this.dom.documentElement.lang = langObj.hreflang;
    });
  }

  ngOnInit(): void {
    this.updateSocialCards();
  }

  /**
   * Handled SEO Related Updates i.e. MetaData, Canonical and HrefLangs
   * @param isRouteChange - HrefLangs needs to be updated incase of route change, hence we use this
  */
  updateSEOData(isRouteChange: boolean) {
    this.updateMetaData();
    this.updateCanonical();
    this.updateSocialCards();
    this.addFaqSchema();
    if (isRouteChange) {
      // THIS NEEDS TO BE CALLED ONLY WHEN ROUTE CHANGES i.e. HOMEPAGE TO STAKING or STAKING TO HOMEPAGE
      this.updateHrefLangs();
      this.updateHiddenUrls();
    }
  }

  updateMetaData() {
    const metaTitle = this.translate.instant(this.onStaking ? 'misc.meta_title_staking' : 'misc.meta_title');
    const metaData = this.translate.instant(this.onStaking ? 'misc.meta_desc_staking' : 'misc.meta_desc');
    this.title.setTitle(metaTitle);
    this.meta.updateTag({ name: 'description', content: metaData });
    this.meta.updateTag({ name: 'robots', content: environment.robots });
  }

  updateCanonical() {
    const link: HTMLLinkElement | null = this.dom.querySelector('link[rel=canonical]');
    let hrefValue = `${this.base}`;

    if (selectedLang !== 'en') {
      hrefValue += `/${selectedLang}`
    }

    if (this.onStaking) {
      hrefValue += `/staking`;
    }

    if (link) {
      link.href = hrefValue;
    } else {
      const newLink = this.dom.createElement('link');
      newLink.rel = 'canonical';
      newLink.href = hrefValue;
      this.dom.head.appendChild(newLink);
    }
  }

  updateHrefLangs() {
    const existingLinks = this.dom.querySelectorAll('link[rel="alternate"]');
    existingLinks.forEach((link: Element) => link.remove());
    for (let lang of allLanguages) {
      const newLink = this.dom.createElement('link');
      newLink.rel = 'alternate';
      newLink.hreflang = lang.hreflang;
      let hrefValue = `${this.base}`;

      if (lang.value !== 'en') {
        hrefValue += `/${lang.value}`
      }

      if (this.onStaking) {
        hrefValue += `/staking`;
      }
      newLink.href = hrefValue;
      this.dom.head.appendChild(newLink);
    }
    const newLink = this.dom.createElement('link');
    newLink.rel = 'alternate';
    newLink.hreflang = 'x-default';
    newLink.href = `${this.base}${this.onStaking ? '/staking' : ''}`;
    this.dom.head.appendChild(newLink);
  }

  addFaqSchema() {
    if (this.onStaking) {
      this.dom.getElementById('FAQ_SCHEMA')?.remove();
      return;
    }
    const faqLists = [...faqItems];
    const keys: string[] = faqLists.flatMap((faq) => {
      return [faq.heading, faq.content]; // no spread needed
    });

    this.translate.get(keys).subscribe((translations) => {
      const faqSchema = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqLists.map((faq) => {
          const question = translations[faq.heading];
          const answer = translations[faq.content] || ''; // fallback to empty if undefined

          return {
            '@type': 'Question',
            name: question,
            acceptedAnswer: {
              '@type': 'Answer',
              text: answer,
            },
          };
        }),
      };
      const existingScript = this.dom.getElementById('FAQ_SCHEMA');
      if (existingScript) {
        this.dom.getElementById('FAQ_SCHEMA')?.remove();
      }

      const script = this.dom.createElement('script');
      script.type = 'application/ld+json';
      script.id = 'FAQ_SCHEMA';
      script.text = JSON.stringify(faqSchema);
      this.dom.head.appendChild(script);
    });
  }

  updateSocialCards(): void {
    const titleText = this.translate.instant(this.onStaking ? 'misc.meta_title_staking' : 'misc.meta_title');
    const description = this.translate.instant(this.onStaking ? 'misc.meta_desc_staking' : 'misc.meta_desc');
    let url = `${this.base}`;

    if (selectedLang && selectedLang !== 'en') {
      url += `/${selectedLang}`
    }

    if (this.onStaking) {
      url += `/staking`;
    }
    const image = `${environment.domain}/assets/images/png/token.png`;

    // Open Graph meta tags
    this.meta.updateTag({ property: 'og:title', content: titleText });
    this.meta.updateTag({ property: 'og:description', content: description });
    this.meta.updateTag({ property: 'og:type', content: 'website' });
    this.meta.updateTag({ property: 'og:url', content: url });
    this.meta.updateTag({ property: 'og:image', content: image });
    this.meta.updateTag({ property: 'og:image:width', content: '600' });
    this.meta.updateTag({ property: 'og:image:height', content: '600' });

    // Twitter Card meta tags
    this.meta.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
    this.meta.updateTag({ name: 'twitter:title', content: titleText });
    this.meta.updateTag({ name: 'twitter:description', content: description });
    this.meta.updateTag({ name: 'twitter:image', content: image });
    this.meta.updateTag({ name: 'twitter:url', content: url });
    this.meta.updateTag({ name: 'twitter:site', content: environment.twitterId });
  }

  updateHiddenUrls() {
    const ul = this.dom.getElementById('hidden-langs') as Element;
    if (ul) {
      while (ul.firstChild) {
        ul.removeChild(ul.firstChild);
      }
    }
    for (let lang of allLanguages) {
      const li = this.dom.createElement('li');
      const anchor = this.dom.createElement('a');
      let hrefValue = `${this.base}`;

      if (lang.value !== 'en') {
        hrefValue += `/${lang.value}`
      }

      if (this.onStaking) {
        hrefValue += `/staking`;
      }

      anchor.href = hrefValue;
      anchor.textContent = `${environment.presaleName} ${lang.name}`;
      li.appendChild(anchor);
      ul.appendChild(li);
    }
  }
}
